import { useEffect, useRef } from 'react';
import { assetUrl, characterAvatarUrl } from '@/lib/assetUrl';
import { escapeHtml, parseChatMarkdown } from '@/lib/chatbotMarkdown';
import type { ChatbotMessage } from '@/hooks/useChatbot';

const RAI_AVATAR = assetUrl('/assets/images/r-ia-profiles.gif');
const DEFAULT_CHAR_ICON = assetUrl('/assets/images/character/icon/14875.webp');

interface ChatMessageProps {
  message: ChatbotMessage;
  charName: string;
  refObjId: number;
}

export function ChatMessage({ message, charName, refObjId }: ChatMessageProps) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className="message user">
        <div className="message-avatar" title={charName}>
          <img
            src={characterAvatarUrl(refObjId)}
            alt={charName}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => {
              e.currentTarget.src = DEFAULT_CHAR_ICON;
            }}
          />
        </div>
        <div className="message-content">{message.content}</div>
      </div>
    );
  }

  return (
    <div className="message assistant">
      <div className="message-avatar" title="R-AI">
        <img
          src={RAI_AVATAR}
          alt="R-AI"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
      <div
        className="message-content markdown-body"
        dangerouslySetInnerHTML={{ __html: parseChatMarkdown(message.content) }}
      />
    </div>
  );
}

export function ChatWelcomeMessage({
  welcomeHtml,
  questionHtml,
}: {
  welcomeHtml: string;
  questionHtml: string;
}) {
  return (
    <div className="message assistant">
      <div className="message-avatar" title="R-AI">
        <img
          src={RAI_AVATAR}
          alt="R-AI"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
      <div className="message-content markdown-body">
        <p dangerouslySetInnerHTML={{ __html: welcomeHtml }} />
        <p dangerouslySetInnerHTML={{ __html: questionHtml }} />
      </div>
    </div>
  );
}

export function ChatTypingIndicator() {
  return (
    <div className="message assistant typing" id="typing-indicator">
      <div className="message-avatar" title="R-AI">
        <img
          src={RAI_AVATAR}
          alt="R-AI"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
      <div className="message-content">
        <div className="typing-indicator">
          <div className="dot" />
          <div className="dot" />
          <div className="dot" />
        </div>
      </div>
    </div>
  );
}

interface ChatMessageListProps {
  messages: ChatbotMessage[];
  charName: string;
  refObjId: number;
  welcomeHtml: string;
  questionHtml: string;
  isWaiting: boolean;
  showWelcome: boolean;
  errorText: string | null;
}

export function ChatMessageList({
  messages,
  charName,
  refObjId,
  welcomeHtml,
  questionHtml,
  isWaiting,
  showWelcome,
  errorText,
}: ChatMessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, isWaiting, errorText]);

  return (
    <div className="chat-messages" id="chat-messages" role="log" aria-live="polite" aria-relevant="additions" ref={containerRef}>
      {showWelcome ? (
        <ChatWelcomeMessage welcomeHtml={welcomeHtml} questionHtml={questionHtml} />
      ) : null}
      {messages.map((msg, index) => (
        <ChatMessage
          key={`${msg.role}-${index}-${msg.content.slice(0, 24)}`}
          message={msg}
          charName={charName}
          refObjId={refObjId}
        />
      ))}
      {errorText ? <ChatErrorMessage text={errorText} /> : null}
      {isWaiting ? <ChatTypingIndicator /> : null}
    </div>
  );
}

export function ChatErrorMessage({ text }: { text: string }) {
  return (
    <div className="message assistant">
      <div className="message-avatar" title="R-AI">
        <img
          src={RAI_AVATAR}
          alt="R-AI"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
      <div
        className="message-content markdown-body"
        dangerouslySetInnerHTML={{
          __html: parseChatMarkdown(`❌ **Error:** ${escapeHtml(text)}`),
        }}
      />
    </div>
  );
}
